import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTenant } from '../contexts/TenantContext'
import { Button } from '../components/Button'
import { Input } from '../components/Input'
import { Card } from '../components/Card'
import { passwordResetApi } from '../services/api'
import logo from '../assets/El CapIT_logo_top_left.png'

export default function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const { tenant } = useTenant()
  const gymLogo = tenant?.branding?.logoUrl
  const gymName = tenant?.branding?.gymName
  const tenantId = tenant?.tenantId || tenant?.id || ''

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await passwordResetApi.forgotPassword(tenantId, email.toLowerCase().trim())
      setSubmitted(true)
    } catch {
      // Always show the success message — backend never reveals if email exists
      setSubmitted(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {gymLogo ? (
            <img src={gymLogo} alt={gymName} className="h-24 mx-auto mb-6 object-contain" />
          ) : (
            <img src={logo} alt="El CapIT" className="h-48 mx-auto mb-6" />
          )}
        </div>

        <Card padding="lg">
          {submitted ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-success-light/20">
                <svg className="w-8 h-8 text-success-DEFAULT" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-neutral-900">Check your email</h2>
              <p className="text-neutral-600">
                If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly. The link expires in 60 minutes.
              </p>
              <p className="text-sm text-neutral-500">Didn't get it? Check your spam folder.</p>
              <Link
                to="/customer/login"
                className="inline-block mt-4 text-sm font-medium text-primary-600 hover:underline"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">Forgot your password?</h2>
              <p className="text-neutral-600 mb-6">
                Enter your email and we'll send you a link to reset your password.
              </p>

              {error && (
                <div className="mb-6 p-4 bg-error-light/20 border border-error-DEFAULT rounded-lg">
                  <p className="text-sm text-error-dark">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={loading || !email}
                  loading={loading}
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>

              <p className="mt-6 text-center text-sm text-neutral-500">
                Remember your password?{' '}
                <Link to="/customer/login" className="font-medium text-primary-600 hover:underline">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </Card>

        {gymName && (
          <p className="text-center text-sm text-neutral-500">{gymName} · Powered by El CapIT<br />A BuilderBias product</p>
        )}
      </div>
    </div>
  )
}
